import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import useScrollReveal from '@/hooks/useScrollReveal';
import { services } from '@/mocks/services';
import { testimonials } from '@/mocks/testimonials';
import FAQSection from '@/pages/home/components/FAQSection';

const trustStats = [
  { icon: 'ri-user-follow-line', label: '85%+ Job Placement Rate' },
  { icon: 'ri-tools-line', label: 'Industry Certifications' },
  { icon: 'ri-community-line', label: '500+ Graduates' },
  { icon: 'ri-time-line', label: '10+ Years Serving DFW' },
  { icon: 'ri-links-line', label: 'Employer Partnerships' },
  { icon: 'ri-star-line', label: '4.9 Rating' },
];

const programFaqs = [
  {
    question: 'Who needs vocational skills training?',
    answer: 'Anyone looking to enter the workforce, change careers, or gain industry-recognized skills can benefit from vocational training at OST-C. Programs are designed for first-time job seekers, career changers, returning citizens, and individuals facing employment barriers who need structured, competency-based training.',
  },
  {
    question: 'How long does a job readiness program take?',
    answer: 'Job readiness and life skills programs can typically be completed in 4–12 weeks depending on your baseline and goals. Vocational certification tracks may range from 3–9 months. OST-C works with each participant to build a timeline that balances thoroughness with efficiency.',
  },
  {
    question: 'Is occupational training covered by any assistance programs?',
    answer: 'OST-C works with participants to identify funding opportunities, grants, and community assistance programs that may offset training costs. Many workforce development and reintegration participants qualify for financial support. Contact our team to explore what may be available to you.',
  },
];

const ServicesPage = () => {
  const heroRef = useScrollReveal();
  const gridRef = useScrollReveal();

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center bg-charcoal overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/31ad2f437c1a083f884483ef495c88da.jpg"
            alt="Occupational skills training programs at OST-C Arlington TX"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/90 to-charcoal/60" />
        </div>
        <div ref={heroRef} className="reveal relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-[2px] bg-gold" />
            <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">Our Programs</span>
          </div>
          <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-5 max-w-3xl">
            Occupational Training Programs Designed for Long-Term Employment
          </h1>
          <p className="font-inter text-base text-white/65 max-w-2xl mb-8 leading-relaxed">
            Targeted vocational training, job readiness coaching, and life skills development — built to produce real employment outcomes and lasting independence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-charcoal font-inter font-bold text-sm px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Enroll Now
              <i className="ri-arrow-right-line" />
            </Link>
            <a
              href="tel:4698535038"
              className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-gold text-white hover:text-gold font-inter font-semibold text-sm px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line" />
              469-853-5038
            </a>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-charcoal border-y border-white/10 py-5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-between gap-y-4">
            {trustStats.map((stat, i) => (
              <div key={i} className="flex items-center gap-2.5 px-3">
                <div className="w-7 h-7 flex items-center justify-center shrink-0">
                  <i className={`${stat.icon} text-gold text-base`} />
                </div>
                <span className="font-inter text-[13px] font-medium text-white/70 whitespace-nowrap">{stat.label}</span>
                {i < trustStats.length - 1 && <div className="hidden lg:block w-[1px] h-5 bg-white/15 ml-3" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="bg-cream py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-[2px] bg-gold" />
              <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">All Programs</span>
              <div className="w-8 h-[2px] bg-gold" />
            </div>
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-charcoal">
              Every Program. One Goal: Employment.
            </h2>
          </div>
          <div ref={gridRef} className="reveal grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service) => (
              <div key={service.id} className="bg-white rounded-lg border border-cream-100 overflow-hidden hover:border-gold/30 transition-colors duration-200">
                <div className="p-8 border-b border-cream-100">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-lg shrink-0">
                      <i className={`${service.icon} text-gold text-xl`} />
                    </div>
                    <div>
                      <h2 className="font-playfair text-2xl font-bold text-charcoal mb-2">{service.title}</h2>
                      <p className="font-inter text-sm text-charcoal/60 leading-relaxed">{service.shortDesc}</p>
                    </div>
                  </div>
                </div>
                <div className="p-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div>
                    <h3 className="font-inter text-xs font-bold text-gold uppercase tracking-widest mb-3">What It Covers</h3>
                    <ul className="flex flex-col gap-2">
                      {service.treats.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-gold text-xs mt-0.5 shrink-0" />
                          <span className="font-inter text-xs text-charcoal/70 leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-inter text-xs font-bold text-gold uppercase tracking-widest mb-3">Who It&apos;s For</h3>
                    <p className="font-inter text-xs text-charcoal/65 leading-relaxed">{service.forWho}</p>
                  </div>
                  <div>
                    <h3 className="font-inter text-xs font-bold text-gold uppercase tracking-widest mb-3">Outcomes</h3>
                    <p className="font-inter text-xs text-charcoal/65 leading-relaxed">{service.outcomes}</p>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-1.5 mt-4 text-gold font-inter text-xs font-semibold hover:gap-2.5 transition-all duration-200 cursor-pointer"
                    >
                      Enroll in This Program <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Answer Block */}
      <section className="bg-charcoal py-20 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="w-16 h-1 bg-gold mb-6" />
            <h2 className="font-playfair text-4xl font-bold text-white leading-tight">
              What Makes OST-C Programs Different?
            </h2>
            <div className="flex flex-wrap gap-3 mt-8">
              {['Employer-Aligned Curriculum', 'Hands-On Training', 'Personalized Plans', 'Job Placement Support', 'Community-Centered'].map((tag) => (
                <span key={tag} className="font-inter text-xs font-medium text-gold border border-gold/30 px-3 py-1.5 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          </div>
          <div>
            <p className="font-inter text-base text-white/70 leading-relaxed mb-5">
              OST-C&apos;s occupational training programs are built around <strong className="text-white">real employment outcomes</strong>, not just certificates. Every program is designed in alignment with employer expectations and regional workforce demand.
            </p>
            <p className="font-inter text-sm text-white/55 leading-relaxed mb-5">
              Participants receive individualized training plans, competency-based skill development, and direct employer connections — creating a complete pipeline from enrollment to employment. Programs integrate life skills and professional readiness to ensure every graduate is prepared not just for a job, but for a career.
            </p>
            <p className="font-inter text-sm text-white/55 leading-relaxed">
              Whether you need vocational certification, job readiness coaching, community reintegration support, or workforce development training, OST-C provides a structured, proven path forward.
            </p>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-cream py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-[2px] bg-gold" />
              <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">The Process</span>
              <div className="w-8 h-[2px] bg-gold" />
            </div>
            <h2 className="font-playfair text-4xl font-bold text-charcoal">Your Path Through Any Program</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { n: '01', title: 'Assessment', desc: 'Understanding your goals, background, and barriers to build the right starting point.', icon: 'ri-focus-3-line' },
              { n: '02', title: 'Program Design', desc: 'A customized training track aligned to your employment goals and timeline.', icon: 'ri-file-list-3-line' },
              { n: '03', title: 'Skills Training', desc: 'Hands-on learning, competency development, and professional readiness coaching.', icon: 'ri-tools-line' },
              { n: '04', title: 'Placement & Support', desc: 'Employer matching, job placement, and post-hire retention support.', icon: 'ri-user-star-line' },
            ].map((step, i) => (
              <div key={step.n} className={`bg-white rounded-lg p-7 border border-cream-100 hover:border-gold/30 transition-colors duration-200 delay-${i * 100}`}>
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-11 h-11 flex items-center justify-center bg-charcoal rounded-lg shrink-0">
                    <i className={`${step.icon} text-gold text-lg`} />
                  </div>
                  <span className="font-playfair text-4xl font-bold text-charcoal/10 leading-none">{step.n}</span>
                </div>
                <h3 className="font-playfair text-lg font-bold text-charcoal mb-2">{step.title}</h3>
                <p className="font-inter text-sm text-charcoal/60 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="bg-white py-20 px-6">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-[2px] bg-gold" />
            <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">Graduate Success</span>
            <div className="w-8 h-[2px] bg-gold" />
          </div>
          <h2 className="font-playfair text-4xl font-bold text-charcoal">What Our Graduates Say</h2>
        </div>
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          {testimonials.slice(0, 2).map((t) => (
            <div key={t.id} className="bg-cream rounded-lg p-8 border border-cream-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-gold" />
              <div className="flex gap-1 mb-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-gold text-xs" />
                ))}
              </div>
              <blockquote className="font-inter text-sm text-charcoal/75 leading-relaxed italic mb-5">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-inter font-semibold text-charcoal text-sm">{t.name}</p>
                  <p className="font-inter text-xs text-charcoal/50">{t.detail}</p>
                </div>
                <span className="font-inter text-xs font-bold text-gold bg-gold/10 border border-gold/20 px-3 py-1 rounded-full">
                  {t.outcome}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <FAQSection faqs={programFaqs} title="Program Questions Answered" subtitle="Common Questions" />

      {/* Final CTA */}
      <section className="bg-charcoal py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-8 h-[2px] bg-gold" />
            <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">Enroll Today</span>
            <div className="w-8 h-[2px] bg-gold" />
          </div>
          <h2 className="font-playfair text-4xl font-bold text-white mb-5">
            Find the Right Program for Your Goals
          </h2>
          <p className="font-inter text-base text-white/60 mb-8">
            Talk to our team about which program fits your needs. We&apos;ll build a training plan around you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-charcoal font-inter font-bold text-sm px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Book Your Enrollment Consultation
              <i className="ri-arrow-right-line" />
            </Link>
            <a
              href="tel:4698535038"
              className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-gold text-white hover:text-gold font-inter font-semibold text-sm px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line" />
              Call 469-853-5038
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default ServicesPage;
